import React, {useState} from "react";
import {Checkmark20Regular, Copy20Regular, Dismiss20Regular} from "@fluentui/react-icons";
import {Button, makeStyles, tokens} from "@fluentui/react-components";


type CopyStatus = "idle" | "copied" | "error";

const useStyles = makeStyles({
    error: {
        color: tokens.colorPaletteRedForeground1,
        ":hover": {
            color: tokens.colorPaletteRedForeground1,
        },
        ":hover:active": {
            color: tokens.colorPaletteRedForeground1,
        },
    },
});


export default function CopyButton() {
    const styles = useStyles();
    const [status, setStatus] = useState<CopyStatus>("idle");

    const handleCopy = async () => {
        if (status !== "idle") return;
        try {
            await copyToClipboard();
            setStatus("copied");
        } catch {
            setStatus("error");
        } finally {
            setTimeout(() => setStatus("idle"), 2500);
        }
    };

    const config: Record<CopyStatus, {
        text: string;
        icon: React.ReactElement;
        className?: string;
    }> = {
        idle: {text: "Copy Editor Content", icon: <Copy20Regular/>},
        copied: {text: "Copied!", icon: <Checkmark20Regular/>},
        error: {text: "Failed", icon: <Dismiss20Regular/>, className: styles.error},
    };

    const {text, icon, className} = config[status];

    return (
        <Button icon={icon} className={className} onClick={() => void handleCopy()}>
            {text}
        </Button>
    );
}

function getHtmlBodyAsync(item: Office.MessageCompose): Promise<string> {
    return new Promise((resolve, reject) => {
        item.body.getAsync(Office.CoercionType.Html, (result) => {
            if (result.status === Office.AsyncResultStatus.Succeeded) {
                resolve(result.value);
            } else {
                reject(new Error(result.error.message));
            }
        });
    });
}

async function copyToClipboard(): Promise<void> {
    const item = Office.context.mailbox.item as Office.MessageCompose | null;
    const html = await getHtmlBodyAsync(item);

    // Try modern API first
    if (navigator.clipboard?.writeText) {
        try {
            await navigator.clipboard.writeText(html);
            return;
        } catch {
            // Fall through to execCommand
        }
    }

    // Fallback for Outlook webview
    const textarea = document.createElement('textarea');
    textarea.value = html;
    textarea.style.cssText = 'position:fixed;top:0;left:0;opacity:0;';
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    try {
        document.execCommand('copy');
    } finally {
        document.body.removeChild(textarea);
    }
}
